import {Dictionary, DrupalSearchApiResponseMeta, DrupalVideoMedia, FacetFilters} from "@/types/hygiena-types";
import {Fragment, useContext, useEffect, useRef, useState} from "react";
import {LoadingSpinnerOverlay} from "../../atoms/loading-spinner";
import {videoSearch} from "@/lib/video-search";
import {useRouter} from "next/router";
import {DocumentSearchFacet} from "../../organisms/widget--document-search";
import {FilterResetButton} from "../../atoms/reset-button";
import {DrupalSearchApiFacet} from "next-drupal";
import classNames from "classnames";
import {DictionaryContext} from "@/context/dictionary-context";
import dynamic from "next/dynamic";
import SectionHeading from "../../organisms/section-heading";
import {CATEGORY_TERM__HOW_TO, CATEGORY_TERM__WEBINAR} from "@/lib/term";
import {Body} from "../../atoms/body";
const TrainingVideosSlider = dynamic(() => import("../../organisms/slider").then((mod) => mod.TrainingVideosSlider));

export function ParagraphSolrVideoSlider({paragraph, ...props}) {
  const router = useRouter();
  const pageLimit = 25;
  const {locale, defaultLocale} = useRouter();
  const [loading, setLoading] = useState<boolean>();
  const [videoCount, setVideoCount] = useState<number>(paragraph?.meta?.count ?? 0);
  const [page, setPage] = useState<number>(0);
  const widgetRef = useRef<HTMLDivElement|null>(null);
  const [activeFilters, setActiveFilters] = useState({} as FacetFilters);
  const [activeVideos, setActiveVideos] = useState<DrupalVideoMedia[]>(paragraph?.training_videos ?? []);
  const [activeMeta, setActiveMeta] = useState<DrupalSearchApiResponseMeta>(paragraph?.meta ?? {});
  const searchTextRef = useRef<HTMLInputElement | null>(null);
  const activeCategory = getActiveCategory();
  const hideFacets: string[] = ["media_product_reference", "video_categories"];
  const productFacet: DrupalSearchApiFacet|null = paragraph?.meta?.facets?.filter(item => item?.id === "media_product_reference")?.[0] ?? null;
  const t = useContext<Dictionary>(DictionaryContext);
  const totalVideos = activeMeta?.count ?? 0;
  const [rebuildKey, setRebuildKey] = useState<number>(Date.now());

  useEffect(() => {
    for (const q of ["fulltext", "field_video_categories", "field_video_industry"]) {
      if (router.query?.[q] && !activeFilters?.[q]) {
        updateFilters(q, router?.query[q]?.toString() ?? "");
      }
    }
  }, [router?.query]);

  if (!paragraph?.training_videos?.length) return <></>

  // Create a dummy facet for the fulltext search field (which is not a facet).
  const titleFacet = {id: "fulltext", path: "fulltext", terms: [], label: t?.video_library?.search_videos};

  /**
   * Scrolls back to the top of the document search.
   *
   * @todo this is used in a few places, refactor it into a reusable function.
   */
  const scrollToRef = () => {
    if (!widgetRef?.current) return;
    const current = widgetRef.current;
    current.scrollIntoView({behavior: 'smooth'})
  }

  /**
   * Performs the video search.
   *
   * @param activeFilters
   * @param pageLimit
   * @param page
   */
  const searchVideos = async (activeFilters: FacetFilters, pageLimit: number, page: number) => {
    const pageOffset = Math.ceil(pageLimit * page);

    // @todo this is really unsustainable if we add more categories, for now, we'll leave it as is, if another category
    //   is tossed into the mix, we'll have to refactor and re-think this.
    let howToFilters = Object.assign({}, activeFilters);
    let webinarFilters = Object.assign({}, activeFilters);
    howToFilters["field_video_categories"] = `${CATEGORY_TERM__HOW_TO}`;
    webinarFilters["field_video_categories"] = `${CATEGORY_TERM__WEBINAR}`;

    const howToResult = await videoSearch(howToFilters, pageLimit, pageOffset, locale, defaultLocale);
    const webinarResult = await videoSearch(webinarFilters, pageLimit, pageOffset, locale, defaultLocale);
    const howtoVideos = howToResult?.data?.videos ?? [];
    const webinarVideos = webinarResult?.data?.videos ?? [];

    let activeCount = 0;
    activeCount += howToResult?.data?.meta?.count ?? 0;
    activeCount += webinarResult?.data?.meta?.count ?? 0;

    let meta = howToResult?.["meta"] ?? {count: 0, facets: []}
    meta.count += webinarResult?.["meta"]?.count ?? 0;
    let facets1 = webinarResult.meta?.facets || [];
    let facets2 = howToResult.meta?.facets || [];
    facets1.map(facet1 => {
      let facet2 = facets2.find(facet2 => facet2.id == facet1.id);
      if(facet2) {
        return {
          ...facet1,
          terms: [...facet1.terms, ...facet2.terms]
        };
      } else {
        return facet1;
      }
    });

    const mergedVideos: DrupalVideoMedia[] = [...howtoVideos, ...webinarVideos];
    const allVideos: DrupalVideoMedia[] = mergedVideos.filter((video, index, self) =>
      index === self.findIndex((v) => v.drupal_internal__mid === video.drupal_internal__mid)
    );

    setActiveVideos(allVideos);
    setActiveMeta(meta);
    setVideoCount(activeCount);
    setRebuildKey(Date.now());
    setLoading(false);
  }

  /**
   * Updates the active filters and performs a new faceted search.
   *
   * @param key
   * @param val
   */
  const updateFilters = async (key: string, val: string) => {
    if (!val || val === "0") {
      delete activeFilters[key];
    }
    else {
      activeFilters[key] = val;
    }
    const path = router.asPath.split("?")[0];
    await router.replace(
      {pathname: path, query: activeFilters},
      undefined,
      {
        shallow: true,
      },
    );
    await setLoading(true);
    await setActiveFilters(activeFilters);
    await setPage(0);
    await searchVideos(activeFilters, pageLimit, 0);
  }


  /**
   * Changes the current page.
   *
   * @param page
   */
  const changePage = async (page) => {
    await setLoading(true);
    scrollToRef();
    await setPage(page);
    await searchVideos(activeFilters, pageLimit, page);
  }

  /**
   * Resets the filters and re-fetches.
   */
  const resetFilters = async () => {
    if (searchTextRef?.current) {
      searchTextRef.current.value = '';
    }
    const path = router.asPath.split("?")[0];
    await setLoading(true);
    await setActiveFilters({});
    await setPage(0);
    await searchVideos({}, pageLimit, 0);
  }

  /**
   * Gets the actively selected category.
   */
  function getActiveCategory(): string {
    if (paragraph?.meta?.facets?.length) {
      const categoryFacet = paragraph?.meta?.facets?.["filter"](item => item?.id === "video_categories")?.[0];
      const activeItem = categoryFacet?.terms?.filter(term => {
        return term?.values?.value === activeFilters?.field_video_categories;
      })?.[0];
      return activeItem?.values?.label ?? "";
    }
    return '';
  }

  const howToVideos = activeVideos?.filter(item => item?.field_video_categories?.drupal_internal__tid?.toString() === CATEGORY_TERM__HOW_TO.toString());
  const webinarVideos = activeVideos?.filter(item => item?.field_video_categories?.drupal_internal__tid?.toString() === CATEGORY_TERM__WEBINAR.toString());

  return (
    <div className="paragraph--solr-video-grid scroll-mt-24" ref={widgetRef}>
      <LoadingSpinnerOverlay loading={loading} fixed={true}/>
      <div className="flex flex-col md:flex-row md:py-6 gap-3 text-black items-center">
        {titleFacet && (
          <DocumentSearchFacet inputClassName="lg:min-w-[400px] !md:max-w-[500px] max-md:w-full" placeholder={t?.video_library?.search_placeholder}
                               autocompleteFacet={productFacet}
                               loading={loading}
                               key={titleFacet.id} facet={titleFacet} activeFilters={activeFilters}
                               setFilters={updateFilters} preFilter={[]} type="text" facetRef={searchTextRef} />
        )}
        {paragraph?.meta?.facets && paragraph?.meta.facets.map(facet => {
          let selectText = t?.documents?.select;
          if (facet?.id === "video_categories") {
            facet.label = t?.documents?.category;
          }
          if (!hideFacets.includes(facet?.id)) {
            const compareFacet = activeMeta?.facets?.filter(item => item?.id === facet?.id)?.[0];
            return <DocumentSearchFacet className="md:w-[320px] md:max-w-none" inputClassName="md:w-[320px]" key={facet.id} facet={facet} activeFilters={activeFilters} setFilters={updateFilters} preFilter={[]} selectText={selectText} compareFacet={compareFacet} />
          }
          return <Fragment key={facet.id}/>
        })}
        <FilterResetButton callback={resetFilters} enabled={!!Object.keys(activeFilters).length} className="self-end mb-2"/>
      </div>
      <h3 className={classNames("font-semibold pt-4 mb-4", {"max-md:hidden": !activeCategory})}>{activeCategory}</h3>
      {!howToVideos?.length && !webinarVideos?.length ? (
        <Body bodyType="lg" className="text-center py-6">{totalVideos} { totalVideos === 1 ? `${t?.video_library?.video_available}` : `${t?.video_library?.videos_available}` }</Body>
      ): <></>}
      {howToVideos?.length ? (
        <div className="mb-12">
          <SectionHeading title={`${t?.video_library?.how_to_videos}`} />
            <TrainingVideosSlider videos={howToVideos} usePath={true} key={`how-to-videos--${rebuildKey}`} />
        </div>
      ): <></>}
      {webinarVideos?.length ? (
        <div className="mb-12">
          <SectionHeading title={`${t?.video_library?.webinar_videos}`} />
            <TrainingVideosSlider videos={webinarVideos} usePath={true} key={`webinar-videos--${rebuildKey}`} />
        </div>
      ): <></>}
    </div>
  )

}